import { useMemo } from "react";
import { useData } from "../context/DataContext";
import {
  months, allocationByMonth, allocationByPrincipal, revenueByStage, revenueByContract,
} from "../lib/calc";
import { fmtMoney, fmtPct } from "../lib/format";
import { downloadCsv } from "../lib/csv";
import { Download } from "lucide-react";

export default function Allocation() {
  const { data, scenario } = useData();
  const m = months(data);

  const byMonth = useMemo(() => allocationByMonth(data, false), [data]);
  const byPrincipal = useMemo(() => allocationByPrincipal(data, false), [data]);
  // Both splits sum the VISIBLE window, so their percentages are taken
  // against the same scenario total the tiles above show.
  const byStage = useMemo(() => revenueByStage(data, false, m), [data, m]);
  const byContract = useMemo(() => revenueByContract(data, false, m), [data, m]);
  const total = byMonth.reduce((s, x) => s + x.value, 0);

  const exportAll = () => {
    const sections = [
      ["By month", [["Month", "Value", "%"], ...byMonth.map((r) => [r.month, r.value, total ? r.value / total : 0])]],
      ["By stage", [["Stage", "Value", "%"], ...byStage.map((r) => [r.stage, r.value, total ? r.value / total : 0])]],
      ["By contract", [["Status", "Value", "%"], ...byContract.map((r) => [r.name, r.value, total ? r.value / total : 0])]],
      ["By principal", [["Principal", "Value", "%"], ...byPrincipal.map((r) => [r.name, r.value, total ? r.value / total : 0])]],
    ];
    const rows = [];
    for (const [title, body] of sections) {
      rows.push([title]);
      rows.push(...body);
      rows.push([]);
    }
    downloadCsv("allocation.csv", rows);
  };

  const scenarioLabel = {
    confirmed: "confirmed",
    confirmed_tentative: "forecast",
  }[scenario] || "forecast";

  return (
    <div className="space-y-8" data-testid="allocation-view">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Where the money sits</div>
          <h2 className="section-h mt-1">Allocation</h2>
          <div className="text-xs font-mono-num text-[var(--soft-fg)] mt-1">
            Scenario: <span className="text-[var(--ink)]">{scenarioLabel}</span>
          </div>
        </div>
        <button
          onClick={exportAll}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
          data-testid="allocation-export-csv"
        >
          <Download size={12} /> CSV
        </button>
      </div>

      <Summary total={total} months={m.length} scenarioLabel={scenarioLabel} />

      <Section title="By month" testid="alloc-by-month">
        <Table
          headers={["Month", "Value", "% of total"]}
          rows={byMonth.map((r) => [
            r.month,
            fmtMoney(r.value),
            total ? fmtPct(r.value / total, 1) : "—",
          ])}
          rightCols={[1, 2]}
        />
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Section title="By stage" testid="alloc-by-stage">
          <Table
            headers={["Stage", "Value", "%"]}
            rows={byStage.map((r) => [r.stage, fmtMoney(r.value), total ? fmtPct(r.value/total, 1) : "—"])}
            rightCols={[1, 2]}
          />
        </Section>

        <Section title="By contract certainty" testid="alloc-by-contract">
          <Table
            headers={["Status", "Value", "%"]}
            rows={byContract.map((r) => [
              <span className={`chip ${r.name==="Yes"?"chip-yes":r.name==="Tentative"?"chip-tentative":"chip-no"}`}>{r.name}</span>,
              fmtMoney(r.value),
              total ? fmtPct(r.value/total, 1) : "—",
            ])}
            rightCols={[1, 2]}
          />
        </Section>
      </div>

      <Section title="By principal" testid="alloc-by-principal">
        <Table
          headers={["Principal", "Value", "%"]}
          rows={byPrincipal.map((r) => [r.name, fmtMoney(r.value), total ? fmtPct(r.value/total, 1) : "—"])}
          rightCols={[1, 2]}
        />
      </Section>
    </div>
  );
}

function Summary({ total, months, scenarioLabel }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 border border-[var(--rule)] divide-y md:divide-y-0 md:divide-x divide-[var(--rule)]">
      <Tile label="Scenario total" value={fmtMoney(total)} testid="alloc-total" />
      <Tile label="Months covered" value={String(months)} testid="alloc-months" />
      <Tile label="Mode" value={scenarioLabel} testid="alloc-mode" />
    </div>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div className="p-6" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className="font-mono-num text-2xl mt-1">{value}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="eyebrow mb-3">{title}</div>
      <div className="border border-[var(--rule)]">{children}</div>
    </section>
  );
}

function Table({ headers, rows, rightCols = [] }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="eyebrow">
          {headers.map((h, i) => (
            <th key={`h-${h}-${i}`} className={`px-4 py-3 ${rightCols.includes(i) ? "text-right" : "text-left"}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r[0] != null ? String(r[0]) : `r-${i}`} className="border-t border-[var(--rule)]">
            {r.map((c, j) => (
              <td key={`${headers[j] || j}`} className={`px-4 py-3 ${rightCols.includes(j) ? "text-right font-mono-num" : ""}`}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
